import type { MapListEntry } from '@kenocyber/shared';
import { useState } from 'react';

/**
 * The map screen.
 *
 * ## It shows locked maps, and says nothing about them
 *
 * All twenty planned maps are listed, because a player should be able to see
 * what the closed alpha contains. A locked one carries exactly what an unbuilt
 * one carries - a number, a name and a scale - so the screen cannot be read as
 * a roadmap or used to work out where anybody else has reached.
 *
 * ## Travel is confirmed, and confirmed once
 *
 * The hunt is settled by the journey, so the player is told that before it
 * happens rather than after. While a journey is in flight every VIAJAR button
 * is disabled: two requests would produce one journey anyway - the server
 * serialises on the progression row - but a second click that appears to do
 * something is a lie about what the game is doing.
 */

export interface MapsPanelProps {
  readonly maps: readonly MapListEntry[];
  readonly currentMap: number;
  readonly travelling: boolean;
  readonly onTravel: (mapIndex: number) => void;
  readonly onClose: () => void;
  /** Set when the last journey was refused. The map has not changed. */
  readonly error?: string | null;
}

/** Basis points to a readable multiplier: 12100 becomes "1,21x". */
function scaleLabel(mobScaleBps: string): string {
  const bps = Number(mobScaleBps);
  if (!Number.isFinite(bps) || bps <= 0) {
    return '—';
  }
  return `${(bps / 10_000).toFixed(2).replace('.', ',')}x`;
}

export function MapsPanel({
  maps,
  currentMap,
  travelling,
  onTravel,
  onClose,
  error = null,
}: MapsPanelProps) {
  const [pending, setPending] = useState<MapListEntry | null>(null);

  return (
    <section className="maps-panel" data-testid="maps-panel" aria-label="Mapas">
      <header className="maps-panel__header">
        <h2>MAPAS</h2>
        <button type="button" data-testid="maps-close" onClick={onClose}>
          Fechar
        </button>
      </header>

      {error === null ? null : (
        <p className="maps-panel__error" role="alert" data-testid="maps-error">
          {error}
        </p>
      )}

      <ul className="maps-panel__list">
        {maps.map((map) => {
          const isCurrent = map.mapIndex === currentMap;
          /*
           * **Três estados, e nunca só por cor** (gate V28I.BQ).
           *
           * Bloqueado pela progressão, desbloqueado e jogável, e desbloqueado
           * mas ainda em produção. O terceiro existe porque `contentReady`
           * deixou de ser uma etiqueta que ninguém lia: ele é o mesmo predicado
           * que a viagem consulta, portanto um mapa que este painel oferecesse
           * e o servidor recusasse seria uma discordância entre dois leitores da
           * mesma função — o que não pode acontecer.
           *
           * Hoje nenhum dos vinte cai no terceiro estado. Ele está aqui porque
           * o dia em que o mapa 21 for planeado e ainda não tiver arte é o dia
           * em que esta linha tem de já existir.
           */
          const inProduction = map.unlocked && !map.contentReady;
          return (
            <li
              key={map.mapIndex}
              className={`maps-panel__item${isCurrent ? ' is-current' : ''}${
                map.unlocked ? '' : ' is-locked'
              }${inProduction ? ' is-unfinished' : ''}`}
              data-testid={`map-${map.mapIndex}`}
              data-current={isCurrent ? 'true' : 'false'}
              data-unlocked={map.unlocked ? 'true' : 'false'}
              data-playable={map.contentReady ? 'true' : 'false'}
            >
              <span className="maps-panel__number">{String(map.mapIndex).padStart(2, '0')}</span>
              <span className="maps-panel__name">{map.name}</span>
              <span className="maps-panel__state">
                {/*
                  Por PALAVRAS, e não por uma cor: quem não distingue o cinzento
                  do dourado tem de conseguir ler a mesma coisa que os outros.
                */}
                {inProduction
                  ? 'Desbloqueado · em produção'
                  : map.unlocked
                    ? 'Desbloqueado'
                    : 'Bloqueado'}
              </span>
              <span className="maps-panel__progress" data-testid={`map-${map.mapIndex}-progress`}>
                {map.bossDefeated ? 'Boss derrotado' : map.unlocked ? 'Em aberto' : '—'}
              </span>
              <span className="maps-panel__scale" data-testid={`map-${map.mapIndex}-scale`}>
                {scaleLabel(map.mobScaleBps)}
              </span>
              {isCurrent ? (
                <span className="maps-panel__here" data-testid={`map-${map.mapIndex}-here`}>
                  MAPA ATUAL
                </span>
              ) : map.unlocked ? (
                <button
                  type="button"
                  data-testid={`map-${map.mapIndex}-travel`}
                  /*
                   * Desactivado, e não escondido: um mapa que a conta GANHOU
                   * continua a ser um mapa que ela tem, e escondê-lo faria a
                   * lista mentir sobre o que foi conquistado. O servidor recusa
                   * na mesma — este botão nunca foi a protecção.
                   */
                  disabled={travelling || inProduction}
                  onClick={() => setPending(map)}
                >
                  VIAJAR
                </button>
              ) : null}
            </li>
          );
        })}
      </ul>

      {pending === null ? null : (
        <div className="maps-panel__confirm" role="dialog" data-testid="maps-confirm">
          <p>A caça atual será liquidada e a nave continuará no mapa escolhido.</p>
          <p className="maps-panel__confirm-target">
            {pending.name} ({String(pending.mapIndex).padStart(2, '0')})
          </p>
          <button
            type="button"
            data-testid="maps-confirm-yes"
            disabled={travelling}
            onClick={() => {
              // The panel stops offering the choice the moment it is taken.
              // The server would serialise a second request anyway; a button
              // that still looks pressable is a lie about what is happening.
              setPending(null);
              onTravel(pending.mapIndex);
            }}
          >
            Viajar
          </button>
          <button
            type="button"
            data-testid="maps-confirm-no"
            disabled={travelling}
            onClick={() => setPending(null)}
          >
            Cancelar
          </button>
        </div>
      )}
    </section>
  );
}
